import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ClipboardCheck, FileText, TrendingUp, Scale, FolderSearch, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ServicesPage = () => {
  const scrollToIntake = () => {
    window.location.assign('/#intake');
  };

  const services = [
    {
      icon: ClipboardCheck,
      title: 'Case Review & Assessment',
      description: 'We review your situation, identify documentation gaps, and help organize your case for a clear appeal submission.',
    },
    {
      icon: FileText,
      title: 'Appeal Documentation Package',
      description: 'Structured summary of account history, events timeline, relevant policies, and supporting materials formatted for clarity.',
    },
    {
      icon: TrendingUp,
      title: 'Business Impact Documentation',
      description: 'Documentation establishing the business context and impact of account restrictions for your appeal records.',
    },
    {
      icon: Scale,
      title: 'Escalation Guidance',
      description: 'Instructions on available appeal and escalation pathways published by platforms, including preparation of formal correspondence.',
    },
    {
      icon: FolderSearch,
      title: 'Evidence Organization',
      description: 'Compilation and labeling of communications, account data, login records, and contextual information for a coherent record.',
    },
    {
      icon: Send,
      title: 'Correspondence Preparation',
      description: 'Preparation of formal written communications for submission through available platform channels.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Back Link */}
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#888680] hover:text-[#d9fb06] transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>

        {/* Hero Section */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-16">
          <div className="max-w-4xl">
            <div className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-6 bg-[#d9fb06]/20 text-[#d9fb06]">
              Our Services
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#d9fb06] mb-6 tracking-tight">
              Documentation & Escalation Support
            </h1>
            <p className="text-xl md:text-2xl text-[#dfddd6] leading-relaxed">
              We help clients organize timelines, evidence, and policy context into <span className="text-[#d9fb06]">structured appeal packages</span> for platform review.
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <div
                key={service.title}
                className="bg-[#302f2c] rounded-2xl p-8 border border-[#3f4816]/50 hover:border-[#d9fb06]/50 transition-colors group"
              >
                <service.icon className="w-12 h-12 text-[#d9fb06] mb-6" />
                <h3 className="text-xl font-bold text-[#dfddd6] mb-4">
                  {service.title}
                </h3>
                <p className="text-[#888680] mb-6 leading-relaxed">
                  {service.description}
                </p>
                <button 
                  onClick={scrollToIntake}
                  className="inline-flex items-center gap-2 text-[#d9fb06] text-sm font-medium group-hover:gap-3 transition-all"
                >
                  Learn More
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Bottom Line */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-16">
          <div className="bg-[#302f2c] rounded-2xl p-8 md:p-12 border border-[#d9fb06]/30">
            <p className="text-xl md:text-2xl text-[#dfddd6] text-center leading-relaxed">
              <span className="text-[#d9fb06] font-bold">Our goal:</span> Organize your case into clear, policy-aware documentation that supports your appeal through <span className="text-[#d9fb06]">available platform channels.</span>
            </p>
            <p className="text-sm text-[#888680] text-center mt-4">
              We do not guarantee outcomes. Platform decisions are outside our control.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-[#dfddd6] mb-6">
            Ready to get started?
          </h2>
          <Button
            onClick={scrollToIntake}
            className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-8 py-6 text-lg font-bold"
          >
            Get Free Evaluation
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ServicesPage;
